import React, { useState, useEffect } from 'react';
import { DownloadSimple, Export, Info, X } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';

let deferredPrompt = null;

/**
 * Discrete top-bar "Install app" pill button.
 * Replaces the old full-width sticky gradient banner. Shows a compact icon-only
 * variant on mobile via the `compact` prop.
 */
export default function PWAInstallButton({ compact = false }) {
  const { t, isDark } = useTheme();
  const [isStandalone, setIsStandalone] = useState(true);
  const [platform, setPlatform] = useState('desktop');
  const [canNativeInstall, setCanNativeInstall] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(!!standalone);
    if (standalone) return;

    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) setPlatform('ios');
    else if (/android/.test(ua)) setPlatform('android');
    else setPlatform('desktop');

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanNativeInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      deferredPrompt = null;
      setCanNativeInstall(false);
    } else {
      setShowInstructions(true);
    }
  };

  if (isStandalone) return null;

  const compactStyle = {
    background: t.accentSoft,
    border: `1px solid ${isDark ? 'rgba(0,229,117,0.3)' : 'rgba(0,229,117,0.35)'}`,
    color: t.accentText,
  };
  const wideStyle = {
    background: t.surfaceAlt,
    border: `1px solid ${t.dividerBorder}`,
    color: t.textPrimary,
  };

  return (
    <>
      <button
        onClick={handleClick}
        className={
          compact
            ? 'w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:opacity-90'
            : 'h-9 px-3 rounded-lg text-xs font-medium flex items-center gap-2 transition-colors hover:opacity-90'
        }
        style={compact ? compactStyle : wideStyle}
        aria-label="Install Divispend app"
        data-testid="pwa-install-button"
      >
        {platform === 'ios' ? (
          <Export className="w-4 h-4" weight="bold" />
        ) : (
          <DownloadSimple className="w-4 h-4" weight="bold" />
        )}
        {!compact && <span>Install app</span>}
        {!compact && canNativeInstall && (
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent }} aria-hidden />
        )}
      </button>

      {showInstructions && (
        <div
          className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInstructions(false)}
          data-testid="pwa-instructions-overlay"
        >
          <div
            className="rounded-2xl w-full max-w-md overflow-hidden"
            style={{ background: t.surface, border: `1px solid ${t.dividerBorder}` }}
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-instructions-modal"
          >
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${t.dividerBorder}` }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: t.accentSoft }}>
                  <DownloadSimple className="w-4 h-4" weight="bold" style={{ color: t.accentText }} />
                </div>
                <h2 className="text-sm font-bold" style={{ color: t.textPrimary }}>Install Divispend</h2>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="hover:opacity-80"
                style={{ color: t.textMutedNew }}
                aria-label="Close"
                data-testid="pwa-instructions-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {platform === 'ios' ? (
                <>
                  <Step num="1" text="Tap the Share button in Safari" iconEl={<Export className="w-4 h-4" style={{ color: t.accentText }} weight="bold" />} />
                  <Step num="2" text={'Scroll down and tap "Add to Home Screen"'} />
                  <Step num="3" text={'Tap "Add" in the top right corner'} />
                </>
              ) : platform === 'android' ? (
                <>
                  <Step num="1" text="Tap the menu icon (three dots) in Chrome" />
                  <Step num="2" text={'Tap "Add to Home screen" or "Install app"'} />
                  <Step num="3" text={'Tap "Install" to confirm'} />
                </>
              ) : (
                <>
                  <Step num="1" text="Click the install icon in your browser address bar" iconEl={<DownloadSimple className="w-4 h-4" style={{ color: t.accentText }} weight="bold" />} />
                  <Step num="2" text={'Or click the menu (three dots) and select "Install Divispend"'} />
                  <Step num="3" text="The app will open in its own window" />
                  <div className="flex items-start gap-2 p-3 rounded-lg mt-2" style={{ background: t.accentSoft, border: `1px solid ${t.dividerBorder}` }}>
                    <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: t.accentText }} />
                    <p className="text-xs" style={{ color: t.textMutedNew }}>Works best in Chrome, Edge, or Brave. Safari on desktop does not support app installation.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Step({ num, text, iconEl }) {
  const { t } = useTheme();
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.accentSoft }}>
        {iconEl || <span className="font-bold text-xs" style={{ color: t.accentText }}>{num}</span>}
      </div>
      <p className="text-sm" style={{ color: t.textPrimary }}>{text}</p>
    </div>
  );
}
